import { Injectable, computed, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { AuthReadyService } from '@core/services/auth/auth-ready.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { User, UserProfile } from '@shared/definitions/user';
import { LEGAL_VERSIONS } from '@core/services/data/legal-versions';
import { withTransientCallableRetry } from '@core/services/data/callable-transient-retry.util';

/** Where the consent was captured — mirrors the backend `consentSourceEnum`. */
export type ConsentSource = 'user_onboarding' | 'reconsent' | 'settings';

export interface RecordConsentOptions {
  source: ConsentSource;
  locale?: 'en' | 'fr' | 'nl' | 'de';
  workspaceId?: string;
}

/**
 * Front door for legal-consent state and the `recordUserConsent` callable.
 *
 * Reads come from `AuthStore.user()?.profile?.consents` (kept in sync by the
 * `users/{uid}` Firestore listener). Writes go through the callable, which
 * appends the immutable audit log and updates the profile summary.
 */
@Injectable({ providedIn: 'root' })
export class LegalConsentService {
  private readonly fns = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly authReady = inject(AuthReadyService);

  /**
   * True when the signed-in user must (re)accept the legal documents — i.e.
   * they have no recorded consent, or the recorded version is older than the
   * current `LEGAL_VERSIONS`. Stays `false` until the user document has been
   * hydrated (`userLoaded`) so a partial user never triggers a false prompt.
   *
   * Scope: this drives the BLOCKING re-consent modal in the authenticated
   * shell, which is exclusively for ALREADY-onboarded accounts whose recorded
   * version is missing or stale (a document version bump, or an account created
   * before the consent feature). Users who have not yet finished user-onboarding
   * accept the documents in the dedicated `user-consent` step — the
   * `OnboardingCompletedGuard` routes them there. We therefore stay silent until
   * `profile.completedAt` is set: otherwise a freshly-signed-up user gets
   * double-prompted with a half-loaded, untranslated modal mid-signup, on top of
   * the onboarding consent step that asks the very same thing.
   */
  readonly needsConsent = computed<boolean>(() => {
    const user = this.authStore.user();
    if (!user || !this.authStore.userLoaded()) return false;
    if (!user.profile?.completedAt) return false;
    return !this.hasCurrentConsent();
  });

  /**
   * True when the hydrated user already has BOTH documents recorded at the
   * current versions — regardless of whether onboarding is finished.
   *
   * `needsConsent` cannot answer that question during onboarding: it stays
   * false until `profile.completedAt` is set, precisely so the blocking
   * re-consent modal never fires mid-signup. The onboarding consent step needs
   * the raw fact instead, to know whether a retry after a partial failure must
   * re-record the acceptance or skip straight to completion (#274).
   */
  readonly hasCurrentConsent = computed<boolean>(() => {
    const user = this.authStore.user();
    if (!user || !this.authStore.userLoaded()) return false;
    const c = user.profile?.consents;
    return (
      c?.terms?.version === LEGAL_VERSIONS.terms &&
      c?.privacy?.version === LEGAL_VERSIONS.privacy
    );
  });

  /**
   * Wake the Gen2 `recordUserConsent` instance while the user reads the
   * legal copy. Sends an intentionally empty payload so Zod rejects before
   * any write — the only goal is to pay the cold start off the Accept click
   * (#597). Failures are swallowed: a warmup that itself gets a 401 still
   * leaves the instance starting for the real call.
   */
  warmupConsentCallable(): void {
    void this.authReady.whenAuthReady().then(async () => {
      try {
        const fn = httpsCallable(this.fns, 'recordUserConsent');
        await fn({});
      } catch {
        // Expected: invalid-argument, or a transient cold-start 401.
      }
    });
  }

  /** Persist acceptance of all legal documents at the current versions. */
  async recordConsent(opts: RecordConsentOptions): Promise<void> {
    await this.authReady.whenAuthReady();
    await withTransientCallableRetry(async () => {
      const fn = httpsCallable(this.fns, 'recordUserConsent');
      await fn({
        termsVersion: LEGAL_VERSIONS.terms,
        privacyVersion: LEGAL_VERSIONS.privacy,
        source: opts.source,
        ...(opts.locale ? { locale: opts.locale } : {}),
        ...(opts.workspaceId ? { workspaceId: opts.workspaceId } : {}),
      });
    });
  }

  /**
   * Optimistically patch `profile.consents` on the locally cached user so
   * `needsConsent()` flips to false immediately — otherwise the re-consent
   * modal could re-open in the window before the Firestore listener catches
   * up. The authoritative value overwrites this on the next snapshot.
   */
  markLocallyAccepted(): void {
    const current = this.authStore.user();
    if (!current) return;

    const now = new Date();
    const entry = (version: string) => ({ version, acceptedAt: now });
    const nextProfile = {
      ...(current.profile ?? {}),
      consents: {
        terms: entry(LEGAL_VERSIONS.terms),
        privacy: entry(LEGAL_VERSIONS.privacy),
        dataResponsibility: entry(LEGAL_VERSIONS.terms),
      },
    } as UserProfile;

    this.authStore.setUser(new User({ ...current, profile: nextProfile }));
  }
}
